import React, { HTMLAttributes, ReactNode } from 'react';

import clsx from 'clsx';

import { Badges, BadgesProps } from '../Badges';
import { Text } from '../Text';
import {
  segmentedContainer,
  segmentedItem,
  segmentedItemIcon,
  segmentedItemText,
} from './Segmented.css';

// 구조화된 Props를 사용하는 아이템
interface StructuredSegmentedItem {
  /** 아이템의 고유 값 */
  value: string;
  /** 표시할 텍스트 */
  text?: string;
  /** 표시할 아이콘 */
  icon?: React.ReactNode;
  /** 배지 속성 */
  badge?: BadgesProps;
  /** 비활성화 여부 */
  disabled?: boolean;
}

// 커스텀 children을 사용하는 아이템
interface CustomSegmentedItem {
  /** 아이템의 고유 값 */
  value: string;
  /** 커스텀 렌더링할 children */
  children: ReactNode;
  /** 비활성화 여부 */
  disabled?: boolean;
}

// 두 타입의 Union
export type SegmentedItemType = StructuredSegmentedItem | CustomSegmentedItem;

// Type guard 함수
const isCustomItem = (item: SegmentedItemType): item is CustomSegmentedItem => {
  return 'children' in item;
};

export interface SegmentedProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** 세그먼트 아이템 목록 */
  items: SegmentedItemType[];
  /** 선택된 값 */
  value?: string;
  /** 값 변경 이벤트 핸들러 */
  onChange?: (value: string) => void;
  /** 전체 비활성화 여부 */
  disabled?: boolean;
}

export const Segmented: React.FC<SegmentedProps> = ({
  items,
  value,
  onChange,
  disabled = false,
  className,
  ...props
}) => {
  const handleItemClick = (itemValue: string, itemDisabled?: boolean) => {
    if (disabled || itemDisabled) {
      return;
    }
    onChange?.(itemValue);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    itemValue: string,
    itemDisabled?: boolean,
  ) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled && !itemDisabled) {
      e.preventDefault();
      onChange?.(itemValue);
    }
  };

  const renderItemContent = (item: SegmentedItemType) => {
    // Custom children을 사용하는 경우
    if (isCustomItem(item)) {
      return item.children;
    }

    // 기존 구조화된 방식
    return (
      <>
        {item.icon && <div className={segmentedItemIcon}>{item.icon}</div>}
        {item.text && (
          <Text textStyle="14_400" className={segmentedItemText}>
            {item.text}
          </Text>
        )}
        {item.badge && <Badges size="s" type="blue" {...item.badge} />}
      </>
    );
  };

  return (
    <div className={clsx(segmentedContainer, className)} role="tablist" {...props}>
      {items.map((item) => {
        const isSelected = value === item.value;
        const isDisabled = disabled || item.disabled;

        return (
          <div
            key={item.value}
            role="tab"
            tabIndex={isDisabled ? -1 : 0}
            aria-selected={isSelected}
            aria-disabled={isDisabled}
            className={segmentedItem({ selected: isSelected, disabled: isDisabled })}
            onClick={() => handleItemClick(item.value, item.disabled)}
            onKeyDown={(e) => handleKeyDown(e, item.value, item.disabled)}
          >
            {renderItemContent(item)}
          </div>
        );
      })}
    </div>
  );
};

Segmented.displayName = 'Segmented';
